/**
 * 새로운 모듈 기반 게임 컨텍스트
 * FirestoreSyncManager를 활용한 실시간 상태 관리
 */

import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback, useRef } from 'react';
import { Room, Player, Question, GameState, GameSettings } from '../types/game';
import { firestoreSyncManager as syncManager } from '../services/FirestoreSyncManager';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

interface GameContextState {
  room: Room | null;
  players: Player[];
  questions: Question[];
  gameState: GameState;
  currentQuestionIndex: number;
  gameSettings: GameSettings;
  hasStarted: boolean;
  phaseStartedAt: number | null;
  phaseDuration: number | null;
  paused: boolean;
  pausedPrevState?: GameState | null;
  resumeGuardUntil?: number | null;
  error?: string | null;
  isLoading?: boolean;
}

interface GameContextValue {
  state: GameContextState;
  actions: {
    createRoom: (subject: string, isPublic: boolean) => Promise<void>;
    joinRoom: (roomCode: string, player: Player) => Promise<{ success: boolean; message?: string }>;
    addQuestion: (question: Question) => void;
    addQuestionsBulk: (questions: Question[]) => void;
    updateQuestion: (question: Question) => void;
    deleteQuestion: (questionId: string) => void;
    reorderQuestions: (questions: Question[]) => void;
    startGame: () => boolean;
    pauseGame: () => void;
    resumeGame: () => boolean;
    nextQuestion: () => void;
    showAnswer: () => void;
    endGame: () => void;
    eliminatePlayer: (playerId: string) => void;
    revivePlayer: (playerId: string) => void;
    kickPlayer: (playerId: string) => void;
    setAnswerDraft: (playerId: string, answer: string | number) => void;
    submitAnswer: (playerId: string, answer: string | number) => void;
    gradeCurrentQuestion: () => void;
    checkAnswer: (playerAnswer: any, question: Question) => boolean;
    updateGameSettings: (settings: Partial<GameSettings>) => void;
    resetGame: () => void;
    updateHostActivity: (roomCode: string) => void;
    adjustTime?: (delta: number) => void;
  };
}

const GameContext = createContext<GameContextValue | null>(null);

const initialGameSettings: GameSettings = {
  timeLimit: 30,
  answerRevealTime: 10,
  eliminationMode: false,
  eliminationThreshold: 3,
  autoMode: true,
  showCorrectAnswer: true,
  allowMultipleAttempts: false,
  randomizeQuestions: false,
  randomizeOptions: false
};

const initialState: GameContextState = {
  room: null,
  players: [],
  questions: [],
  gameState: 'waiting',
  currentQuestionIndex: 0,
  gameSettings: initialGameSettings,
  hasStarted: false,
  phaseStartedAt: null,
  phaseDuration: null,
  paused: false,
  pausedPrevState: null,
  resumeGuardUntil: null
};

export function NewGameProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GameContextState>(initialState);
  const { user } = useAuth();
  const isHost = useMemo(() => {
    const uid = user?.uid || (typeof window !== 'undefined' ? (window as any).firebaseAuthUid : null);
    const hostId = state.room?.hostId;
    return !!(hostId && uid && hostId === uid);
  }, [user?.uid, state.room?.hostId]);

  const autoProgressTimersRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const draftSyncTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const draftAnswersRef = useRef<Map<string, string>>(new Map());
  const reconnectAttemptedRef = useRef<boolean>(false); // 재연결 시도 플래그

  const clearAllAutoProgressTimers = useCallback(() => {
    autoProgressTimersRef.current.forEach(timer => clearTimeout(timer));
    autoProgressTimersRef.current.clear();
  }, []);

  const clearDraftSyncTimers = useCallback(() => {
    draftSyncTimersRef.current.forEach(timer => clearTimeout(timer));
    draftSyncTimersRef.current.clear();
  }, []);

  const setAutoTimer = useCallback((delaySeconds: number, callback: () => void) => {
    clearAllAutoProgressTimers();
    const timer = setTimeout(() => {
      callback();
      autoProgressTimersRef.current.delete(timer);
    }, delaySeconds * 1000);
    autoProgressTimersRef.current.add(timer);
  }, [clearAllAutoProgressTimers]);

  useEffect(() => {
    // SyncManager는 싱글톤이므로 이미 연결된 방이 있으면 즉시 상태 반영
    // 코드 변경(HMR) 시에도 연결을 유지하고 즉시 상태를 복구
    const gameData = syncManager.getGameData();
    const draftAnswersMap = draftAnswersRef.current;
    
    // 이미 연결된 방이 있으면 즉시 상태 설정 (loading 방지)
    if (gameData.room) {
    setState(prev => ({
      ...prev,
      room: gameData.room,
      players: gameData.players,
      questions: gameData.questions,
      gameState: gameData.gameState,
      currentQuestionIndex: gameData.currentQuestionIndex,
      gameSettings: gameData.gameSettings || prev.gameSettings,
      hasStarted: typeof (gameData as any).hasStarted === 'boolean' ? (gameData as any).hasStarted : false,
      phaseStartedAt: (gameData as any).phaseStartedAt ?? null,
      phaseDuration: (gameData as any).phaseDuration ?? null,
      paused: (gameData as any).paused ?? false,
      pausedPrevState: gameData.pausedPrevState ?? null,
      isLoading: false
    }));
    } else {
      // 방이 없으면 초기 상태 설정
      setState(prev => ({
        ...prev,
        room: null,
        players: [],
        questions: [],
        gameState: 'waiting',
        currentQuestionIndex: 0,
        gameSettings: prev.gameSettings,
        hasStarted: false,
        phaseStartedAt: null,
        phaseDuration: null,
        paused: false,
        pausedPrevState: null,
        isLoading: false
      }));
    }

    // SyncManager 리스너만 사용 (eventBus 중복 제거)
    const syncListeners: Array<{type: string; cb: Function}> = [];
    
    // 타이머 관리 (cleanup을 위해 저장)
    const effectTimers: Set<NodeJS.Timeout> = new Set();
    
    // 실시간 업데이트 리스너 추가
    const handleGameDataUpdate = (gameData: any) => {
      setState(prev => {
        // finished 상태일 때 점수 데이터 보호
        // endGame에서 설정한 점수가 Firestore 동기화 과정에서 손실되지 않도록 보호
        let playersToUse = gameData.players;
        
        // finished 상태로 전환되거나 이미 finished 상태인 경우 점수 보호
        if (gameData.gameState === 'finished') {
          const incomingTotalScore = (gameData.players || []).reduce((sum: number, p: any) => sum + (p.score || 0), 0);
          const prevTotalScore = (prev.players || []).reduce((sum: number, p: any) => sum + (p.score || 0), 0);
          
          // finished로 처음 전환되는 경우
          if (prev.gameState !== 'finished') {
            // 들어온 데이터의 총 점수가 0이면 이전 점수 유지 (endGame 호출 전 상태)
            if (incomingTotalScore === 0 && prevTotalScore > 0) {
              console.warn('[SYNC] finished 전환 시 점수 손실 감지, 이전 점수 유지');
              playersToUse = prev.players;
            }
          } else {
            // 이미 finished 상태인 경우, 점수가 0으로 초기화된 데이터가 오면 이전 점수 유지
            if (incomingTotalScore === 0 && prevTotalScore > 0) {
              console.warn('[SYNC] finished 상태에서 점수 손실 감지, 이전 점수 유지');
              playersToUse = prev.players;
            }
          }
        }
        
        // 방 데이터가 있으면 로딩 완료
        const isLoading = gameData.room ? false : prev.isLoading;
        
        return {
          ...prev,
          room: gameData.room,
          players: playersToUse, // 보호된 플레이어 데이터 사용
          questions: gameData.questions,
          gameState: gameData.gameState,
          currentQuestionIndex: gameData.currentQuestionIndex,
          gameSettings: gameData.gameSettings || prev.gameSettings,
          hasStarted: typeof gameData.hasStarted === 'boolean' ? gameData.hasStarted : false,
          phaseStartedAt: gameData.phaseStartedAt ?? null,
          phaseDuration: gameData.phaseDuration ?? null,
          paused: gameData.paused ?? false,
          pausedPrevState: gameData.pausedPrevState ?? null,
          isLoading // 방 데이터가 있으면 로딩 완료
        };
      });
    };
    
    // 리스너 등록을 즉시 실행
    syncManager.addEventListener('GAME_DATA_UPDATE', handleGameDataUpdate);
    syncListeners.push({ type: 'GAME_DATA_UPDATE', cb: handleGameDataUpdate });
    

    // 새로고침 복원: localStorage에서 방 코드 확인
    // 재연결 플래그를 사용하여 중복 재연결 방지
    const saved = (() => { 
      try { 
        const code = localStorage.getItem('currentRoomCode');
        console.log('🔄 [CONTEXT] localStorage 확인:', code);
        if (code) {
          return code;
        }
      } catch (e) {
        console.warn('🔄 [CONTEXT] localStorage 읽기 실패:', e);
      }
      return null;
    })();
    
    console.log('🔄 [CONTEXT] 재연결 체크:', {
      saved,
      hasRoom: !!gameData.room,
      reconnectAttempted: reconnectAttemptedRef.current
    });
    
    // 저장된 방 코드로 재연결 시도 (이미 연결되어 있지 않은 경우만, 한 번만 시도)
    if (saved && !gameData.room && !reconnectAttemptedRef.current) {
      console.log('🔄 [CONTEXT] 재연결 시작:', saved);
      reconnectAttemptedRef.current = true; // 재연결 시도 플래그 설정
      
      // 재연결 시도 중 로딩 상태 설정
      setState(prev => ({ ...prev, isLoading: true }));
      
      (async () => {
        try {
          console.log('🔄 [CONTEXT] Firestore에서 방 확인 시작:', saved);
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          const roomRef = doc(db, 'game_rooms', saved);
          const roomDoc = await getDoc(roomRef);
          
          if (roomDoc.exists()) {
            const roomData = roomDoc.data() as any;
            const hostId = roomData.hostId || roomData.room?.hostId;
            const currentUid = user?.uid || (typeof window !== 'undefined' ? (window as any).firebaseAuthUid : null);
            const isHost = currentUid && hostId === currentUid;
            
            // 참가자 정보 확인 (localStorage에서)
            let savedPlayer: Player | null = null;
            try {
              const playerStr = localStorage.getItem('currentPlayer');
              if (playerStr) {
                savedPlayer = JSON.parse(playerStr);
              }
            } catch (e) {
              console.warn('🔄 [CONTEXT] 플레이어 정보 읽기 실패:', e);
            }
            
            console.log('🔄 [CONTEXT] 방 데이터 확인:', {
              saved,
              hostId,
              currentUid,
              isHost,
              hasSavedPlayer: !!savedPlayer
            });
            
            // 호스트이거나 참가자 정보가 있으면 재연결 시도
            if (isHost || savedPlayer) {
              const reconnectType = isHost ? '호스트' : '참가자';
              console.log(`🔄 [CONTEXT] ${reconnectType} 재연결 시도:`, saved);
              
              await syncManager.connectToRoom(saved);
              console.log('🔄 [CONTEXT] connectToRoom 완료, 리스너 대기 중...');
              
              // 리스너가 데이터를 받을 때까지 대기 (최대 3초, 100ms 간격으로 체크)
              let attempts = 0;
              const maxAttempts = 30; // 3초
              while (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 100));
                const reconnectedData = syncManager.getGameData();
                if (reconnectedData.room) {
                  console.log(`🔄 [CONTEXT] ${reconnectType} 재연결 성공, 상태 반영:`, reconnectedData.room.code);
                  setState(prev => ({
                    ...prev,
                    room: reconnectedData.room,
                    players: reconnectedData.players,
                    questions: reconnectedData.questions,
                    gameState: reconnectedData.gameState,
                    currentQuestionIndex: reconnectedData.currentQuestionIndex,
                    gameSettings: reconnectedData.gameSettings || prev.gameSettings,
                    hasStarted: typeof reconnectedData.hasStarted === 'boolean' ? reconnectedData.hasStarted : false,
                    phaseStartedAt: reconnectedData.phaseStartedAt ?? null,
                    phaseDuration: reconnectedData.phaseDuration ?? null,
                    paused: reconnectedData.paused ?? false,
                    pausedPrevState: (reconnectedData as any).pausedPrevState ?? null,
                    isLoading: false
                  }));
                  return;
                }
                attempts++;
              }
              
              // 타임아웃 후에도 방 데이터가 없으면
              const finalData = syncManager.getGameData();
              if (finalData.room) {
                console.log(`🔄 [CONTEXT] ${reconnectType} 타임아웃 후 재확인 성공:`, finalData.room.code);
                setState(prev => ({
                  ...prev,
                  room: finalData.room,
                  players: finalData.players,
                  questions: finalData.questions,
                  gameState: finalData.gameState,
                  currentQuestionIndex: finalData.currentQuestionIndex,
                  gameSettings: finalData.gameSettings || prev.gameSettings,
                  hasStarted: typeof finalData.hasStarted === 'boolean' ? finalData.hasStarted : false,
                  phaseStartedAt: finalData.phaseStartedAt ?? null,
                  phaseDuration: finalData.phaseDuration ?? null,
                  paused: finalData.paused ?? false,
                  pausedPrevState: (finalData as any).pausedPrevState ?? null,
                  isLoading: false
                }));
              } else {
                console.warn(`🔄 [CONTEXT] ${reconnectType} 재연결 타임아웃, 방 데이터가 없음`);
                setState(prev => ({ ...prev, isLoading: false }));
                reconnectAttemptedRef.current = false; // 재시도 가능하도록 플래그 리셋
              }
              return;
            } else {
              console.log('🔄 [CONTEXT] 호스트도 아니고 참가자 정보도 없음, localStorage 정리');
              // 방이 없거나 권한이 없으면 localStorage 정리
              localStorage.removeItem('currentRoomCode');
              localStorage.removeItem('currentPlayer');
              setState(prev => ({ ...prev, isLoading: false }));
              reconnectAttemptedRef.current = false;
            }
          } else {
            console.log('🔄 [CONTEXT] 방이 존재하지 않음, localStorage 정리');
            // 방이 없으면 localStorage 정리
            localStorage.removeItem('currentRoomCode');
            setState(prev => ({ ...prev, isLoading: false }));
            reconnectAttemptedRef.current = false;
          }
        } catch (e) {
          console.error('🔄 [CONTEXT] 재연결 실패:', e);
          localStorage.removeItem('currentRoomCode');
          setState(prev => ({ ...prev, isLoading: false }));
          reconnectAttemptedRef.current = false; // 재시도 가능하도록 플래그 리셋
        }
      })();
    } else if (!saved && !gameData.room) {
      // 저장된 방 코드가 없고 방도 없으면 로딩 완료
      console.log('🔄 [CONTEXT] 재연결할 방 없음, 로딩 완료');
      setState(prev => ({ ...prev, isLoading: false }));
    }

    return () => {
      // 모든 타이머 정리
      effectTimers.forEach(timer => clearTimeout(timer));
      effectTimers.clear();
      clearDraftSyncTimers();
      draftAnswersMap.clear();
      
      // SyncManager 리스너 해제
      syncListeners.forEach(({ type, cb }) => {
        try {
          (syncManager as { removeEventListener?: (type: string, cb: Function) => void }).removeEventListener?.(type, cb);
        } catch {}
      });
      
      // 중요: 싱글톤 매니저는 연결을 유지해야 함
      // cleanup에서 disconnect()를 호출하지 않음:
      // 1. 코드 변경(HMR) 시에도 연결 유지
      // 2. 페이지 이동 시 자동으로 연결 해제됨
      // 3. 실제 언마운트(페이지 이동) 시에만 연결이 끊어짐
      // 
      // 주의: disconnect()는 페이지 이동 시에만 호출되어야 함
      // 개발 환경의 HMR이나 프로덕션의 컴포넌트 재렌더링에서는 연결 유지
      
      // 자동 진행 타이머도 정리 (컴포넌트 언마운트 시)
      clearAllAutoProgressTimers();
    };
  }, [clearAllAutoProgressTimers, clearDraftSyncTimers, user?.uid]);

  // 헬퍼 함수들 - useCallback으로 메모이제이션
  
  // 답안이 유효한지 확인하는 공통 함수
  const hasValidAnswer = useCallback((rawAnswer: any, questionType: string): boolean => {
    // 숫자 타입인 경우 (객관식 인덱스 0 포함)
    if (typeof rawAnswer === 'number' && !isNaN(rawAnswer)) {
      return true;
    }
    
    // null/undefined 체크
    if (rawAnswer === null || rawAnswer === undefined) {
      return false;
    }
    
    const answerStr = String(rawAnswer);
    
    // OX 문제의 경우 'O' 또는 'X'만 유효
    if (questionType === 'ox') {
      const trimmed = answerStr.trim().toUpperCase();
      return trimmed === 'O' || trimmed === 'X';
    }
    
    // 단답형 또는 일반 문자열 답안: 빈 문자열이 아니면 유효
    return answerStr.trim().length > 0;
  }, []);
  
  // 답안을 정규화하는 공통 함수
  const normalizeAnswer = useCallback((rawAnswer: any): string => {
    if (typeof rawAnswer === 'number') {
      return String(rawAnswer); // 숫자 0도 "0"으로 변환
    }
    if (rawAnswer !== undefined && rawAnswer !== null && rawAnswer !== '') {
      return String(rawAnswer);
    }
    return ''; // 답안이 없으면 빈 문자열
  }, []);
  
  // 플레이어 답안을 통합하는 공통 함수 (draft answer 우선순위 처리)
  const mergePlayerAnswers = useCallback((player: Player, draftAnswers: Map<string, string | number>, currentQuestion: Question): Player => {
    // 이미 제출한 경우 제출된 답안 사용
    if (player.hasSubmitted && player.currentAnswer !== undefined && player.currentAnswer !== null && player.currentAnswer !== '') {
      return player;
    }
    
    // 로컬 draft answer가 있으면 사용
    const draft = draftAnswers.get(player.id);
    if (draft !== undefined && draft !== null) {
      // 객관식의 경우 숫자 0도 유효한 답안
      if (currentQuestion.type === 'multiple' && typeof draft === 'number' && draft === 0) {
        return { ...player, currentAnswer: String(draft) };
      }
      // 빈 문자열이 아닌 경우만 사용
      if (draft !== '') {
        return { ...player, currentAnswer: typeof draft === 'number' ? String(draft) : draft };
      }
    }
    
    // hasSubmitted가 false이지만 currentAnswer가 서버에 동기화된 경우
    if (!player.hasSubmitted && player.currentAnswer !== undefined && player.currentAnswer !== null && player.currentAnswer !== '') {
      // 답안이 유효한지 확인
      if (hasValidAnswer(player.currentAnswer, currentQuestion.type)) {
        return player;
      }
    }
    
    return player;
  }, [hasValidAnswer]);


      // 플레이어들의 hasSubmitted 상태를 서버에 동기화하는 공통 함수
  const syncPlayerSubmissions = useCallback((players: Player[]): void => {
    const latestState = syncManager.getGameData();
    
    players.forEach((player: Player) => {
      // hasSubmitted가 true이고 답안이 있으면 동기화
      // 빈 문자열도 체크 (객관식 인덱스 0은 "0"이므로 유효)
      const hasValidAnswer = player.currentAnswer !== undefined && 
                             player.currentAnswer !== null && 
                             player.currentAnswer !== '';
      
      if (player.hasSubmitted && hasValidAnswer) {
        // 서버의 현재 값과 비교하여 다를 때만 동기화 (중복 신호 방지)
        const serverPlayer = latestState.players.find(p => p.id === player.id);
        if (serverPlayer && 
            serverPlayer.hasSubmitted === true && 
            serverPlayer.currentAnswer === player.currentAnswer) {
          // 이미 동일한 값으로 동기화되어 있으면 스킵
          if (process.env.NODE_ENV === 'development') {
            logger.debug('[SYNC] 서버 값과 동일, 동기화 스킵:', {
              playerId: player.id,
              nickname: player.nickname
            });
          }
          return;
        }
        
        syncManager.updatePlayer(player.id, { 
          hasSubmitted: true, 
          currentAnswer: player.currentAnswer 
        } as any).catch(error => {
          logger.warn('[SYNC] 플레이어 제출 상태 동기화 실패:', { 
            playerId: player.id, 
            nickname: player.nickname,
            error 
          });
        });
      } else if (player.hasSubmitted && !hasValidAnswer) {
        // hasSubmitted가 true인데 답안이 없으면 로그만 남김 (이상 상황)
        logger.warn('[SYNC] hasSubmitted가 true인데 답안이 없음:', {
          playerId: player.id,
          nickname: player.nickname,
          currentAnswer: player.currentAnswer
        });
      }
    });
  }, []);
  
  // 정답 체크 함수: 타입 안전한 비교 로직
  const checkAnswer = useCallback((playerAnswer: any, question: Question): boolean => {
    if (!question) {
      logger.warn('[CHECK] 문제가 없음');
      return false;
    }
    
    // 플레이어 답안이 없거나 null/undefined면 오답
    // 단, 객관식 인덱스 0은 유효한 답안이므로 주의
    if (playerAnswer === null || playerAnswer === undefined) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[CHECK] 답안이 null/undefined');
      }
      return false;
    }
    
    // 빈 문자열 체크 (단, 숫자 0은 유효하므로 제외)
    if (playerAnswer === '' && typeof playerAnswer === 'string') {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[CHECK] 답안이 빈 문자열');
      }
      return false;
    }
    
    const correctAnswer = question.correctAnswer;
    
    // 객관식의 경우: 숫자 인덱스로 비교
    if (question.type === 'multiple') {
      // correctAnswer는 숫자 인덱스 (0, 1, 2, ...)
      // playerAnswer는 숫자 또는 문자열로 변환된 숫자일 수 있음
      let playerNum: number;
      if (typeof playerAnswer === 'number') {
        playerNum = playerAnswer;
      } else {
        const trimmed = String(playerAnswer).trim();
        // 빈 문자열이면 오답
        if (trimmed === '') {
          if (process.env.NODE_ENV === 'development') {
            logger.debug('[CHECK] 객관식 답안이 빈 문자열');
          }
          return false;
        }
        playerNum = parseInt(trimmed, 10);
      }
      
      let correctNum: number;
      if (typeof correctAnswer === 'number') {
        correctNum = correctAnswer;
      } else {
        const trimmed = String(correctAnswer).trim();
        correctNum = parseInt(trimmed, 10);
      }
      
      // NaN 체크
      if (isNaN(playerNum) || isNaN(correctNum)) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[CHECK] 객관식 숫자 변환 실패:', { playerNum, correctNum, playerAnswer, correctAnswer });
        }
        return false;
      }
      
      const isCorrect = playerNum === correctNum;
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[CHECK] 객관식 비교:', { playerNum, correctNum, isCorrect });
      }
      return isCorrect;
    }
    
    // OX 문제의 경우: 문자열 비교 (대소문자 구분 없음)
    if (question.type === 'ox') {
      const playerStr = String(playerAnswer).trim().toUpperCase();
      const correctStr = String(correctAnswer).trim().toUpperCase();
      const isCorrect = playerStr === correctStr;
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[CHECK] OX 비교:', { 
          playerAnswer, 
          correctAnswer,
          playerStr, 
          correctStr, 
          isCorrect,
          questionId: question.id 
        });
      }
      return isCorrect;
    }
    
    // 단답형의 경우: 공백 제거 후 문자열 비교 (대소문자 구분)
    if (question.type === 'short') {
      const playerStr = String(playerAnswer).trim();
      const correctStr = String(correctAnswer).trim();
      const isCorrect = playerStr === correctStr;
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[CHECK] 단답형 비교:', { playerStr, correctStr, isCorrect });
      }
      return isCorrect;
    }
    
    // 기본값: 문자열 비교
    const playerStr = String(playerAnswer).trim();
    const correctStr = String(correctAnswer).trim();
    return playerStr === correctStr;
  }, []);

  // 플레이어 채점을 수행하는 공통 함수 (checkAnswer 이후에 선언)
  const gradePlayer = useCallback((player: Player, currentQuestion: Question, gameSettings: GameSettings): Player => {
    const rawAnswer = player.currentAnswer;
    const hasAnswer = hasValidAnswer(rawAnswer, currentQuestion.type);
    
    // answerToCheck 생성: number 타입도 고려 (0도 유효한 답안)
    let answerToCheck: any;
    if (rawAnswer !== undefined && rawAnswer !== null) {
      // number 타입이거나 빈 문자열이 아닌 경우
      if (typeof rawAnswer === 'number' || (typeof rawAnswer === 'string' && rawAnswer !== '')) {
        answerToCheck = rawAnswer;
      } else {
        answerToCheck = normalizeAnswer(rawAnswer);
      }
    } else {
      answerToCheck = normalizeAnswer(rawAnswer);
    }
    
    const isCorrect = hasAnswer ? checkAnswer(answerToCheck, currentQuestion) : false;
    const shouldEliminate = gameSettings?.eliminationMode && !isCorrect;
    const finalAnswer = normalizeAnswer(rawAnswer);
    
    // 점수 계산: 정답이면 문제 배점만큼 추가
    const newScore = isCorrect ? player.score + currentQuestion.score : player.score;
    
    // hasSubmitted 업데이트: 답안이 있으면 제출로 간주
    const newHasSubmitted = hasAnswer || player.hasSubmitted;

    // 개발 모드에서만 상세 로그 출력 (프로덕션에서는 제거)
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[GRADE] 채점 상세:', {
        playerId: player.id,
        nickname: player.nickname,
        questionId: currentQuestion.id,
        questionScore: currentQuestion.score,
        rawAnswer,
        hasAnswer,
        isCorrect,
        scoreBefore: player.score,
        scoreAfter: newScore,
        hasSubmittedBefore: player.hasSubmitted,
        hasSubmittedAfter: newHasSubmitted
      });
    }

    return {
      ...player,
      currentAnswer: finalAnswer,
      hasSubmitted: newHasSubmitted,
      score: newScore,
      isEliminated: shouldEliminate || player.isEliminated
    };
  }, [hasValidAnswer, normalizeAnswer, checkAnswer]);

  // 시간 계산 헬퍼 함수 - phaseDuration은 항상 초 단위로 가정
  // 반환값도 초 단위
  const calculateRemainingTime = (phaseStartedAt: number | null, phaseDuration: number | null, paused: boolean): number => {
    if (!phaseStartedAt || !phaseDuration) return 0;
    
    if (paused) {
      return phaseDuration; // 일시정지 중에는 phaseDuration 값 그대로 사용 (초 단위)
    }
    
    // 진행 중일 때: 경과 시간을 계산하여 남은 시간 구하기
    const elapsed = Math.floor((Date.now() - phaseStartedAt) / 1000); // ms를 초로 변환
    return Math.max(0, phaseDuration - elapsed); // 초 단위 반환
  };

  // 마지막 문제 완료 처리: 단일 진입점 (모든 경로에서 이 함수만 호출)
  const finalizeAsWaitingPaused = useCallback((completedIndex: number) => {
    clearAllAutoProgressTimers(); // 모든 타이머 즉시 정리
    
    const finishNowMs = Date.now();
    
    setState(prev => ({
      ...prev,
      gameState: 'waiting',
      paused: true,
      phaseStartedAt: finishNowMs, // ms 단위 타임스탬프
      phaseDuration: 0, // 초 단위 (대기 상태)
      currentQuestionIndex: completedIndex // 완료된 마지막 문제 인덱스
    }));
    
    syncManager.updateGameData({
      gameState: 'waiting',
      paused: true,
      phaseStartedAt: finishNowMs,
      phaseDuration: 0,
      hasStarted: true,
      currentQuestionIndex: completedIndex // 완료된 마지막 문제 인덱스
    } as any);
    
  }, [clearAllAutoProgressTimers]);

  // 호스트 측 안전장치는 actions 정의 후 별도 useEffect로 이동

  // 다음 문제 번호 계산 함수 (중복 제거)
  const getNextQuestionNumber = useCallback((): number => {
    const currentState = syncManager.getGameData();
    if (currentState.questions.length === 0) return 1;
    
    const existingNumbers = currentState.questions
      .map(q => {
        const match = q.id.match(/^q_(\d+)_/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(num => num > 0);
    
    return existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
  }, []);

  const actions = {
    setAnswerDraft: (playerId: string, answer: string | number) => {
      const currentState = syncManager.getGameData();
      // 이미 제출한 플레이어는 답안 변경 불가
      const player = currentState.players.find(p => p.id === playerId);
      if (player && player.hasSubmitted) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[DRAFT] 이미 제출된 플레이어, 답안 변경 불가:', { playerId, nickname: player.nickname });
        }
        return;
      }

      // 객관식의 경우 숫자 인덱스를 문자열로 변환 (일관성 유지)
      // OX와 단답형은 문자열 그대로 사용
      const answerStr = answer !== null && answer !== undefined 
        ? String(answer) 
        : '';
      
      // 현재 문제 타입 확인
      const currentQuestion = currentState.questions[currentState.currentQuestionIndex];
      const isShortAnswer = currentQuestion?.type === 'short';
      
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[DRAFT] 답안 저장:', {
          playerId,
          nickname: player?.nickname,
          answer,
          answerStr,
          questionIndex: currentState.currentQuestionIndex,
          isShortAnswer
        });
      }
      
      // draft answer는 즉시 저장 (로컬 상태)
      draftAnswersRef.current.set(playerId, answerStr);

      // 주관식의 경우: 즉시 setState를 호출하지 않고 debounce 후에만 호출
      // (입력 중 상태 업데이트로 인한 충돌 방지)
      // 객관식/OX의 경우: 즉시 setState 호출 (선택이므로 즉시 반영 필요)
      if (!isShortAnswer) {
        const updatedPlayers = currentState.players.map(player =>
          player.id === playerId 
            ? { ...player, currentAnswer: answerStr }
            : player
        );
        setState(prev => ({ ...prev, players: updatedPlayers }));
      }

      const existingTimer = draftSyncTimersRef.current.get(playerId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(() => {
        // 현재 상태를 다시 확인하여 이미 제출한 플레이어는 보호
        const latestState = syncManager.getGameData();
        const latestPlayer = latestState.players.find(p => p.id === playerId);
        
        // 이미 제출한 플레이어는 draft answer를 서버에 동기화하지 않음
        if (latestPlayer && latestPlayer.hasSubmitted) {
          logger.debug('[DRAFT] 이미 제출된 플레이어, 동기화 스킵:', {
            playerId,
            nickname: latestPlayer.nickname
          });
          draftSyncTimersRef.current.delete(playerId);
          return;
        }
        
        // 게임 상태 확인: playing 상태가 아니면 동기화하지 않음
        if (latestState.gameState !== 'playing') {
          logger.debug('[DRAFT] 게임이 playing 상태가 아님, 동기화 스킵:', {
            playerId,
            gameState: latestState.gameState
          });
          draftSyncTimersRef.current.delete(playerId);
          return;
        }
        
        // 현재 문제 인덱스 확인: 다른 문제로 넘어갔으면 동기화하지 않음
        if (latestState.currentQuestionIndex !== currentState.currentQuestionIndex) {
          if (process.env.NODE_ENV === 'development') {
            logger.debug('[DRAFT] 문제가 변경됨, 동기화 스킵:', {
              playerId,
              currentIndex: currentState.currentQuestionIndex,
              latestIndex: latestState.currentQuestionIndex
            });
          }
          draftSyncTimersRef.current.delete(playerId);
          return;
        }
        
        // 주관식의 경우: debounce 후에 로컬 상태 업데이트 (입력 완료 후)
        // 이렇게 하면 입력 중에는 selectedAnswer가 덮어쓰이지 않음
        if (isShortAnswer) {
          const updatedPlayers = latestState.players.map(player =>
            player.id === playerId 
              ? { ...player, currentAnswer: answerStr }
              : player
          );
          setState(prev => ({ ...prev, players: updatedPlayers }));
        }
        
        // 서버의 현재 값과 비교하여 다를 때만 동기화 (중복 신호 방지)
        const serverCurrentAnswer = latestPlayer?.currentAnswer;
        if (serverCurrentAnswer === answerStr) {
          if (process.env.NODE_ENV === 'development') {
            logger.debug('[DRAFT] 서버 값과 동일, 동기화 스킵:', {
              playerId,
              answerStr
            });
          }
          draftSyncTimersRef.current.delete(playerId);
          return;
        }
        
        // draft answer만 동기화 (hasSubmitted는 변경하지 않음)
        // hasSubmitted는 showAnswer나 gradeCurrentQuestion에서만 업데이트
        const updates: Partial<Player> = { currentAnswer: answerStr };
        syncManager.updatePlayer(playerId, updates)
          .then(() => {
            if (process.env.NODE_ENV === 'development') {
              logger.debug('[DRAFT] 답안 동기화 성공:', {
                playerId,
                answerStr,
                questionIndex: latestState.currentQuestionIndex
              });
            }
          })
          .catch(error => {
            logger.warn('[DRAFT] 플레이어 답안 동기화 실패:', {
              playerId,
              nickname: latestPlayer?.nickname,
              answerStr,
              error: error?.message || error
            });
            // 동기화 실패해도 draft answer는 유지 (showAnswer에서 통합됨)
          })
          .finally(() => {
            draftSyncTimersRef.current.delete(playerId);
          });
      }, 500);

      draftSyncTimersRef.current.set(playerId, timer);
    },
    createRoom: async (subject: string, isPublic: boolean) => {
      try {
        const currentState = syncManager.getGameData();
        setState(prev => ({ ...prev, isLoading: true }));
        
        // 현재 로그인한 사용자의 UID를 hostId로 사용
        const hostId = (window as { firebaseAuthUid?: string }).firebaseAuthUid || `host_${Date.now()}`;
        
        // 방 생성 (roomManager 대신 직접 구현)
        const roomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
        const now = Date.now();
        const room: Room = {
          id: roomCode,
          code: roomCode,
          subject: subject.trim(),
          isPublic,
          hostId,
          players: [],
          questions: [],
          gameState: 'waiting',
          currentQuestionIndex: 0,
          gameSettings: {
            timeLimit: 30,
            answerRevealTime: 10,
            eliminationMode: false,
            eliminationThreshold: 3,
            autoMode: true,
            showCorrectAnswer: true,
            allowMultipleAttempts: false,
            randomizeQuestions: false,
            randomizeOptions: false
          },
          hasStarted: false,
          phaseStartedAt: null,
          phaseDuration: null,
          paused: false,
          createdAt: now,
          updatedAt: now
        };
        
        // 기본 예시 문제 3개(OX/객관식/주관식)
        const defaultQuestions: Question[] = [
          {
            id: `q_1_${now}`,
            type: 'ox',
            question: '태양은 서쪽에서 뜬다.',
            score: 10,
            timeLimit: 10,
            options: ['O', 'X'],
            correctAnswer: 'X'
          },
          {
            id: `q_2_${now}`,
            type: 'multiple',
            question: '대한민국의 수도는?',
            score: 20,
            timeLimit: 15,
            options: ['서울', '부산', '대구', '인천'],
            correctAnswer: 0
          },
          {
            id: `q_3_${now}`,
            type: 'short',
            question: '3 x 7 = ?',
            score: 30,
            timeLimit: 20,
            correctAnswer: '21'
          }
        ];

        // Firestore에 방 생성 및 연결
        try {
          await syncManager.createRoom(room);
          
          // 생성 직후 즉시 확인 (1초, 3초, 5초, 10초) - 디버그용
          // 주의: async 함수 내부에서 실행되므로 cleanup에서 접근 불가
          // 실패해도 기능에 영향 없으므로 그대로 실행됨
          const checkTimes = [1000, 3000, 5000, 10000];
          checkTimes.forEach(delay => {
            setTimeout(async () => {
              try {
                const { doc, getDoc } = await import('firebase/firestore');
                const { db } = await import('../config/firebase');
                const roomRef = doc(db, 'game_rooms', roomCode);
                const roomDoc = await getDoc(roomRef);
                
                if (roomDoc.exists()) {
                  // 디버그 로그 제거됨
                } else {
                  // 디버그 로그 제거됨
                }
              } catch (e) {
                // 에러 무시 (디버그용)
              }
            }, delay);
          });
          
        } catch (createError) {
          throw createError;
        }
        
        // 초기 게임 데이터 설정
        await syncManager.updateGameData({ 
          room, 
          players: [], 
          questions: defaultQuestions,
          gameState: 'waiting',
          currentQuestionIndex: 0,
          hasStarted: false,
          gameSettings: currentState.gameSettings
        });

        setState(prev => ({ 
          ...prev, 
          room, 
          players: [], 
          questions: defaultQuestions,
          gameState: 'waiting',
          currentQuestionIndex: 0,
          hasStarted: false,
          gameSettings: prev.gameSettings,
          isLoading: false
        }));
      } catch (error) {
        setState(prev => ({ 
          ...prev, 
          error: '방 생성에 실패했습니다.',
          isLoading: false 
        }));
      }
    },

    joinRoom: async (roomCode: string, player: Player) => {
      try {
        // Firestore 방에 연결 후 참여 처리
        await (syncManager as any).connectToRoom(roomCode);
        await (syncManager as any).addPlayer(player);

        // 상태는 실시간 리스너에서 최신 데이터로 반영됨
        return { success: true };
      } catch (error: any) {
        // 닉네임 중복 에러는 별도 처리
        let message = '방 참여에 실패했습니다.';
        if (typeof error?.message === 'string' && error.message.includes('닉네임')) {
          message = error.message;
        } else if (typeof error?.message === 'string') {
          message = error.message;
        }

        setState(prev => ({ ...prev, error: message }));
        return { success: false, message };
      }
    },

    addQuestion: (question: Question) => {
      setState(prev => {
        // 문제 ID가 없으면 자동 생성
        const questionWithId = question.id ? question : {
          ...question,
          id: `q_${getNextQuestionNumber()}_${Date.now()}`
        };
        
        const newQuestions = [...prev.questions, questionWithId];
        
        // 게임 상태 처리
        let newGameState = prev.gameState;
        let newCurrentIndex = prev.currentQuestionIndex;
        let shouldResetGame = false;
        
        // 마지막 문제를 완료한 상태에서 새 문제 추가 시
        const allQuestionsFinished = prev.currentQuestionIndex >= prev.questions.length - 1 && 
                                      prev.gameState === 'waiting' && 
                                      prev.paused;
        
        if (allQuestionsFinished) {
          // 모든 문제 완료 상태에서 새 문제 추가: 완료 이력과 인덱스를 보존
          // - gameState는 그대로 waiting 유지
          // - currentQuestionIndex는 마지막 완료 인덱스 유지
          // - 플레이어 상태 리셋 없음
          newGameState = 'waiting';
          newCurrentIndex = prev.currentQuestionIndex; // 유지
          shouldResetGame = false;
        }
        // finished 상태면 waiting으로
        else if (prev.gameState === 'finished') {
          newGameState = 'waiting';
          newCurrentIndex = 0;
        }
        
        // 낙관적 UI 업데이트: 즉시 로컬 상태 반영
        
        // Firestore는 백그라운드에서 (깜빡임 방지)
        setTimeout(() => {
          const updateData: any = {
            questions: newQuestions,
            gameState: newGameState,
            currentQuestionIndex: newCurrentIndex,
            players: shouldResetGame ? prev.players.map(p => {
              // undefined 필드는 제거하여 Firestore 오류 방지
              const { currentAnswer, ...playerWithoutAnswer } = p;
              return {
                ...playerWithoutAnswer,
                hasSubmitted: false
              };
            }) : prev.players,
            room: prev.room
          };
          
          // error 필드는 undefined이면 제거
          if (shouldResetGame && updateData.error === undefined) {
            delete updateData.error;
          }
          
          syncManager.updateGameData(updateData);
        }, 0);
        
        return { ...prev, questions: newQuestions, gameState: newGameState, currentQuestionIndex: newCurrentIndex };
      });
    },

    addQuestionsBulk: (questionsToAdd: Question[]) => {
      if (!questionsToAdd || questionsToAdd.length === 0) return;
      setState(prev => {
        // 문제들에 ID가 없으면 자동 생성
        let nextNumber = getNextQuestionNumber();
        const questionsWithIds = questionsToAdd.map(question => 
          question.id ? question : {
            ...question,
            id: `q_${nextNumber++}_${Date.now()}`
          }
        );
        
        const newQuestions = [...prev.questions, ...questionsWithIds];
        
        // 게임 상태 처리
        let newGameState = prev.gameState;
        let newCurrentIndex = prev.currentQuestionIndex;
        let shouldResetGame = false;
        
        // 마지막 문제를 완료한 상태에서 새 문제 추가 시
        const allQuestionsFinished = prev.currentQuestionIndex >= prev.questions.length - 1 && 
                                      prev.gameState === 'waiting' && 
                                      prev.paused;
        
        if (allQuestionsFinished) {
          // 모든 문제 완료 상태에서 새 문제 일괄 추가: 완료 이력과 인덱스를 보존
          newGameState = 'waiting';
          newCurrentIndex = prev.currentQuestionIndex; // 유지
          shouldResetGame = false;
        }
        // finished 상태면 waiting으로
        else if (prev.gameState === 'finished') {
          newGameState = 'waiting';
          newCurrentIndex = 0;
        }
        
        // 낙관적 UI 업데이트: 즉시 로컬 상태 반영
        
        // Firestore는 백그라운드에서 (깜빡임 방지)
        setTimeout(() => {
          const updateData: any = {
            questions: newQuestions,
            gameState: newGameState,
            currentQuestionIndex: newCurrentIndex,
            players: shouldResetGame ? prev.players.map(p => {
              // undefined 필드는 제거하여 Firestore 오류 방지
              const { currentAnswer, ...playerWithoutAnswer } = p;
              return {
                ...playerWithoutAnswer,
                hasSubmitted: false
              };
            }) : prev.players,
            room: prev.room
          };
          
          // error 필드는 undefined이면 제거
          if (shouldResetGame && updateData.error === undefined) {
            delete updateData.error;
          }
          
          syncManager.updateGameData(updateData);
        }, 0);
        
        return { ...prev, questions: newQuestions, gameState: newGameState, currentQuestionIndex: newCurrentIndex };
      });
    },

    updateQuestion: (updatedQuestion: Question) => {
      setState(prev => {
        const newQuestions = prev.questions.map(q => 
          q.id === updatedQuestion.id ? updatedQuestion : q
        );
        
        // 낙관적 UI 업데이트: 즉시 로컬 상태 반영
        
        // Firestore는 백그라운드에서 (깜빡임 방지)
        setTimeout(() => {
          syncManager.updateGameData({
            questions: newQuestions,
            gameState: prev.gameState,
            currentQuestionIndex: prev.currentQuestionIndex,
            players: prev.players,
            room: prev.room
          } as any);
        }, 0);
        
        return { ...prev, questions: newQuestions };
      });
    },

    deleteQuestion: (questionId: string) => {
      const currentState = syncManager.getGameData();
      const newQuestions = currentState.questions.filter(q => q.id !== questionId);
      
      // 낙관적 UI 업데이트: 즉시 로컬 상태 반영
      setState(prev => ({ ...prev, questions: newQuestions }));
      
      // Firestore 업데이트는 백그라운드에서 (깜빡임 방지)
      setTimeout(() => {
        syncManager.updateGameData({ questions: newQuestions });
      }, 0);
    },

    reorderQuestions: (questions: Question[]) => {
      // 낙관적 UI 업데이트: 즉시 로컬 상태 반영
      setState(prev => ({ ...prev, questions }));
      
      // Firestore는 백그라운드에서 (깜빡임 방지)
      setTimeout(() => {
        syncManager.updateGameData({ questions });
      }, 0);
    },

    startGame: () => {
      const currentState = syncManager.getGameData();
      if (currentState.questions.length === 0) {
        setState(prev => ({ ...prev, error: '문제를 먼저 추가해주세요.' }));
        return false;
      }

      // 게임 시작 가능 여부 확인
      const canStart = currentState.gameState === 'waiting' || 
                       currentState.gameState === 'paused' ||
                       currentState.gameState === 'finished';
      
      if (!canStart) {
        return false;
      }
      
      // 마지막 문제를 모두 완료한 상태인지 확인
      // currentQuestionIndex는 항상 마지막 인덱스 이하이므로
      // waiting + paused 상태에서 새 문제가 없으면 완료 상태
      const lastQuestionIndex = currentState.questions.length - 1;
      const allQuestionsFinished = currentState.gameState === 'waiting' && 
                                   currentState.paused && 
                                   currentState.currentQuestionIndex >= lastQuestionIndex;
      
      if (allQuestionsFinished) {
        setState(prev => ({ ...prev, error: '모든 문제를 완료했습니다. 새 문제를 추가해주세요.' }));
        return false;
      }
      
      // 재시작 여부 판단: finished 상태이거나 게임 시작 전이거나 마지막 문제 완료 상태
      const isRestart = currentState.gameState === 'finished' || 
                       !currentState.hasStarted ||
                       (currentState.currentQuestionIndex > lastQuestionIndex);
      const startIndex = isRestart ? 0 : currentState.currentQuestionIndex;
      
      // 플레이어 리셋
      draftAnswersRef.current.clear();
      clearDraftSyncTimers();

      const resetPlayers = currentState.players.map(player => ({
        ...player,
        score: isRestart ? 0 : player.score,
        hasSubmitted: false,
        currentAnswer: undefined,
        isEliminated: isRestart ? false : player.isEliminated
      }));

      const nowMs = Date.now();
      const newState = {
        gameState: 'playing' as const,
        currentQuestionIndex: startIndex,
        hasStarted: true,
        phaseStartedAt: nowMs, // ms 단위 타임스탬프
        phaseDuration: currentState.gameSettings.timeLimit, // 초 단위
        paused: false,
        players: resetPlayers
      };

      // 상태 업데이트와 Firestore 업데이트를 하나의 배치로 처리 (경합 방지)
      setState(prev => ({ ...prev, ...newState }));
      // 중요 업데이트: gameState, players, currentQuestionIndex 모두 포함 → 버전 증가
      syncManager.updateGameData(newState as any);
      
      // 자동 진행
      if (currentState.gameSettings?.autoMode) {
        setAutoTimer(currentState.gameSettings.timeLimit, () => {
          const latest = syncManager.getGameData();
          if (latest.gameState === 'playing' && !latest.paused) {
            actions.showAnswer();
          }
        });
      }
      
      return true;
    },

    pauseGame: () => {
      const currentState = syncManager.getGameData();
      
      // 호스트 권한 및 상태 검증
      if (currentState.gameState === 'paused') {
        return;
      }
      
      // 자동 진행 타이머 정리
      clearAllAutoProgressTimers();
      
      const remainingTime = calculateRemainingTime(
        currentState.phaseStartedAt, 
        currentState.phaseDuration, 
        false  // 일시정지 전이므로 현재 진행 중인 게임의 남은 시간 계산
      );
      
      // 최소 1초 보장 (초 단위)
      const finalRemainingTime = Math.max(1, remainingTime);
      
      const nowMs = Date.now();
      const newState = {
        gameState: 'paused' as const,
        paused: true,
        pausedPrevState: currentState.gameState,
        phaseStartedAt: nowMs,  // ms 단위 타임스탬프
        phaseDuration: finalRemainingTime // 초 단위
      };
      
      // 상태 업데이트와 Firestore 업데이트를 하나의 배치로 처리 (경합 방지)
      setState(prev => ({ ...prev, ...newState }));
      // 중요 업데이트: gameState 변경 → 버전 증가
      syncManager.updateGameData(newState as any);
      
    },

    resumeGame: () => {
      const currentState = syncManager.getGameData();
      
      // 호스트 권한 및 상태 검증
      // paused 상태이거나, waiting + paused 상태인 경우 재개 가능
      const canResume = currentState.gameState === 'paused' || 
                       (currentState.gameState === 'waiting' && currentState.paused);
      
      if (!canResume) {
        return false;
      }
      
      const nowMs = Date.now();
      const remainingTime = currentState.phaseDuration || 0;
      const restoredState = currentState.pausedPrevState || 'playing';
      
      // waiting + paused 상태에서는 새 문제 추가 여부에 따라 처리
      if (currentState.gameState === 'waiting' && currentState.paused) {
        // 현재 인덱스는 마지막 문제이므로, 추가된 문제가 있는지 확인
        const nextIndex = currentState.currentQuestionIndex + 1;
        
        if (nextIndex >= currentState.questions.length) {
          // 아직 새 문제가 없으면 대기 상태 유지 및 오류 메시지
          setState(prev => ({ ...prev, error: '모든 문제를 완료했습니다. 새 문제를 추가해주세요.' }));
          return false;
        }
        
        // 새 문제가 있으면 이어서 시작
        draftAnswersRef.current.clear();
        clearDraftSyncTimers();

        const resetPlayers = currentState.players.map(player => ({
          ...player,
          hasSubmitted: false,
          currentAnswer: undefined
          // 점수와 탈락 상태는 유지
        }));
        
        const newState = {
          gameState: 'playing' as const,
          currentQuestionIndex: nextIndex,
          hasStarted: true,
          phaseStartedAt: nowMs, // ms 단위 타임스탬프
          phaseDuration: currentState.gameSettings.timeLimit, // 초 단위
          paused: false,
          pausedPrevState: null,
          players: resetPlayers
        };
        
        // 상태 업데이트와 Firestore 업데이트를 하나의 배치로 처리 (경합 방지)
        setState(prev => ({ ...prev, ...newState }));
        // 중요 업데이트: gameState, players, currentQuestionIndex 모두 포함 → 버전 증가
        syncManager.updateGameData(newState as any);
        
        // 자동 진행
        if (currentState.gameSettings?.autoMode) {
          setAutoTimer(currentState.gameSettings.timeLimit, () => {
            const latest = syncManager.getGameData();
            if (latest.gameState === 'playing' && !latest.paused) {
              actions.showAnswer();
            }
          });
        }
        
        return true;
      }
      
      // 일반적인 재개 로직
      const newState = {
        gameState: restoredState,
        paused: false,
        pausedPrevState: null,
        phaseStartedAt: nowMs,  // ms 단위 타임스탬프
        phaseDuration: remainingTime  // 초 단위 (남은 시간 유지)
      };
      
      // 상태 업데이트와 Firestore 업데이트를 하나의 배치로 처리 (경합 방지)
      setState(prev => ({ ...prev, ...newState }));
      // 중요 업데이트: gameState 변경 → 버전 증가
      syncManager.updateGameData(newState as any);
      
      // 자동 진행 타이머 재설정
      if (currentState.gameSettings?.autoMode && remainingTime > 0 && restoredState !== 'showingAnswer') {
        // showingAnswer 상태에서는 자동 진행 안 함 (마지막 문제 완료 처리 때문)
        setAutoTimer(remainingTime, () => {
          const latest = syncManager.getGameData();
          if (latest.gameState === restoredState && !latest.paused) {
            if (restoredState === 'playing') {
              actions.showAnswer();
            }
          }
        });
      }
      
      return true;
    },

    nextQuestion: () => {
      const currentState = syncManager.getGameData();
      
      // playing 상태에서 넘기기 = 정답 공개로 이동
      if (currentState.gameState === 'playing') {
        actions.showAnswer();
        return;
      }
      
      // showingAnswer가 아니면 실행 불가
      if (currentState.gameState !== 'showingAnswer') {
        return;
      }
      
      // 현재 문제가 채점되지 않았다면 먼저 채점 수행 (안전장치)
      const currentQuestion = currentState.questions[currentState.currentQuestionIndex];
      if (currentQuestion && currentState.lastGradedQuestionId !== currentQuestion.id) {
        logger.warn('[NEXT] 다음 문제로 넘어가기 전에 현재 문제가 채점되지 않았습니다. 채점을 수행합니다.');
        actions.gradeCurrentQuestion();
        // 채점 후 상태를 다시 가져옴
        const updatedState = syncManager.getGameData();
        if (updatedState.gameState !== 'showingAnswer') {
          return; // 상태가 변경되었으면 중단
        }
      }
      
      draftAnswersRef.current.clear();
      clearDraftSyncTimers();

      // 1단계: 현재 문제 완료 처리 (인덱스 증가)
      const completedIndex = currentState.currentQuestionIndex; // 현재 완료한 문제
      const nextIndex = completedIndex + 1; // 다음 문제 인덱스
      
      // 2단계: 다음 문제가 있는지 확인
      if (nextIndex >= currentState.questions.length) {
        // 문제가 더 없음 → 대기 상태로 전환 (완료된 인덱스 전달)
        finalizeAsWaitingPaused(completedIndex);
        return;
      }
      
      // 3단계: 다음 문제로 진행
      const resetPlayers = currentState.players.map(player => ({
        ...player,
        hasSubmitted: false,
        currentAnswer: undefined
      }));

      const nowMs = Date.now();
      const newState = {
        gameState: 'playing' as const,
        currentQuestionIndex: nextIndex,
        phaseStartedAt: nowMs, // ms 단위 타임스탬프
        phaseDuration: currentState.gameSettings.timeLimit, // 초 단위
        paused: false,
        players: resetPlayers
      };

      // 상태 업데이트와 Firestore 업데이트를 하나의 배치로 처리 (경합 방지)
      setState(prev => ({ ...prev, ...newState }));
      // 중요 업데이트: gameState, players, currentQuestionIndex 모두 포함 → 버전 증가
      syncManager.updateGameData(newState as any);
      
      // 자동 진행 타이머 설정
      if (currentState.gameSettings?.autoMode) {
        setAutoTimer(currentState.gameSettings.timeLimit, () => {
          const latest = syncManager.getGameData();
          if (latest.gameState === 'playing' && !latest.paused) {
            actions.showAnswer();
          }
        });
      }
    },

    showAnswer: () => {
      const latest = syncManager.getGameData();
      if (latest.gameState !== 'playing') {
        return;
      }

      const currentQuestion = latest.questions[latest.currentQuestionIndex];
      if (!currentQuestion) {
        return;
      }

      // draft answer 동기화 타이머 정리 (중복 제출 방지)
      clearDraftSyncTimers();
      
      // 주관식의 경우 draft answer를 먼저 제출 처리 (한 번만)
      // 이미 제출된 플레이어는 제외
      const playersToSubmit = latest.players || [];
      const submittedPlayerIds = new Set<string>();
      const updatedPlayersForSubmit: Player[] = [];
      
      // 주관식 draft answer를 로컬에서 직접 제출 처리 (서버 동기화는 나중에 일괄 처리)
      playersToSubmit.forEach((player: Player) => {
        // 이미 제출한 플레이어는 그대로 유지
        if (player.hasSubmitted) {
          submittedPlayerIds.add(player.id);
          updatedPlayersForSubmit.push(player);
          return;
        }
        
        // draft answer가 있고 유효한 경우 제출 처리
        const draftAnswer = draftAnswersRef.current.get(player.id);
        if (draftAnswer !== undefined && draftAnswer !== null && draftAnswer !== '') {
          // 주관식의 경우에만 자동 제출 (객관식/OX는 이미 선택 시 제출됨)
          if (currentQuestion.type === 'short') {
            const answerStr = typeof draftAnswer === 'number' ? String(draftAnswer) : draftAnswer;
            // 한 번만 제출되도록 플래그 설정
            if (!submittedPlayerIds.has(player.id)) {
              submittedPlayerIds.add(player.id);
              // 로컬 상태 직접 업데이트 (서버 동기화는 나중에 일괄 처리)
              updatedPlayersForSubmit.push({
                ...player,
                hasSubmitted: true,
                currentAnswer: answerStr
              });
              // draft answer 제거
              draftAnswersRef.current.delete(player.id);
            } else {
              updatedPlayersForSubmit.push(player);
            }
          } else {
            updatedPlayersForSubmit.push(player);
          }
        } else {
          updatedPlayersForSubmit.push(player);
        }
      });

      // 제출된 플레이어 상태를 로컬에 반영하고 서버에도 즉시 동기화
      if (updatedPlayersForSubmit.length > 0) {
        setState(prev => ({ ...prev, players: updatedPlayersForSubmit }));
        
        // 주관식으로 제출된 플레이어들을 서버에 즉시 동기화
        updatedPlayersForSubmit.forEach((p: Player) => {
          const serverPlayer = latest.players.find((lp: Player) => lp.id === p.id);
          const wasSubmitted = serverPlayer?.hasSubmitted;
          // 서버의 현재 값과 비교하여 다를 때만 동기화 (중복 신호 방지)
          if (p.hasSubmitted && !wasSubmitted && p.currentAnswer) {
            // 서버에 이미 동일한 값이 있는지 확인
            if (serverPlayer && 
                serverPlayer.hasSubmitted === true && 
                serverPlayer.currentAnswer === p.currentAnswer) {
              // 이미 동일한 값으로 동기화되어 있으면 스킵
              if (process.env.NODE_ENV === 'development') {
                logger.debug('[SHOWANSWER] 서버 값과 동일, 동기화 스킵:', {
                  playerId: p.id,
                  nickname: p.nickname
                });
              }
              return;
            }
            
            // 제출된 플레이어의 답안을 서버에 즉시 동기화
            syncManager.updatePlayer(p.id, { 
              hasSubmitted: true, 
              currentAnswer: p.currentAnswer 
            } as any).catch(error => {
              logger.error('[SHOWANSWER] 주관식 답안 서버 동기화 실패:', { 
                playerId: p.id, 
                error 
              });
            });
          }
        });
      }
      
      // 제출 반영된 플레이어 목록 사용
      const playersSource = updatedPlayersForSubmit.length > 0 ? updatedPlayersForSubmit : latest.players || [];
      
      // draft answer와 제출된 answer를 통합 (공통 함수 사용)
      const playersWithAnswers = playersSource.map((player: Player) => {
        const merged = mergePlayerAnswers(player, draftAnswersRef.current, currentQuestion);
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[SHOWANSWER] 답안 통합:', {
            playerId: player.id,
            nickname: player.nickname,
            originalAnswer: player.currentAnswer,
            draftAnswer: draftAnswersRef.current.get(player.id),
            mergedAnswer: merged.currentAnswer,
            hasSubmitted: merged.hasSubmitted
          });
        }
        return merged;
      });

      const alreadyGraded = (latest.lastGradedQuestionId || null) === currentQuestion.id;
      
      // 중복 채점 방지: 이미 채점된 문제인지 확인
      if (alreadyGraded) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[SHOWANSWER] 이미 채점된 문제:', {
            questionId: currentQuestion.id,
            lastGradedQuestionId: latest.lastGradedQuestionId
          });
        }
      }

      let gradedPlayers = playersWithAnswers;
      if (!alreadyGraded) {
        // 새로운 채점 수행 (공통 함수 사용)
        gradedPlayers = playersWithAnswers.map((player: Player) => {
          // 채점 전 점수 저장 (중복 점수 추가 방지 확인용)
          const scoreBefore = player.score;
          
          // 이미 이 문제에 대해 채점된 플레이어는 스킵 (중복 채점 방지)
          // lastGradedQuestionId가 현재 문제 ID와 같으면 이미 채점된 것으로 간주
          // 하지만 이미 채점된 경우는 위에서 alreadyGraded로 처리되므로 여기서는 추가 체크 불필요
          
          const graded = gradePlayer(player, currentQuestion, latest.gameSettings || state.gameSettings);
          
          // 점수 증가량 검증 (중복 점수 부여 방지)
          const scoreIncrease = graded.score - scoreBefore;
          const expectedIncrease = graded.score > scoreBefore ? currentQuestion.score : 0;
          
          // 점수 증가량이 예상과 다르면 경고 및 수정
          if (scoreIncrease !== expectedIncrease && scoreIncrease > 0) {
            logger.warn('[GRADE] 점수 증가량 이상, 수정:', {
              playerId: player.id,
              nickname: player.nickname,
              scoreBefore,
              scoreAfter: graded.score,
              scoreIncrease,
              expectedIncrease,
              questionScore: currentQuestion.score
            });
            // 잘못된 점수 증가량이면 예상 증가량으로 수정
            const correctedScore = scoreBefore + expectedIncrease;
            return {
              ...graded,
              score: correctedScore
            };
          }
          
          if (process.env.NODE_ENV === 'development') {
            logger.debug('[GRADE] 플레이어 채점:', {
              playerId: player.id,
              nickname: player.nickname,
              questionType: currentQuestion.type,
              questionId: currentQuestion.id,
              rawAnswer: player.currentAnswer,
              answerToCheck: player.currentAnswer,
              hasAnswer: hasValidAnswer(player.currentAnswer, currentQuestion.type),
              correctAnswer: currentQuestion.correctAnswer,
              isCorrect: graded.score > player.score,
              scoreBefore: player.score,
              scoreAfter: graded.score,
              scoreIncrease
            });
          }
          
          return graded;
        });
      } else {
        // 이미 채점된 문제이지만, hasSubmitted 상태는 업데이트해야 함
        // (새로운 답안이 추가되었거나 동기화 지연으로 인한 상태 불일치 방지)
        gradedPlayers = playersWithAnswers.map((player: Player) => {
          const rawAnswer = player.currentAnswer;
          const hasAnswer = hasValidAnswer(rawAnswer, currentQuestion.type);
          
          // 답안이 있으면 hasSubmitted를 true로 업데이트 (점수는 변경하지 않음)
          if (hasAnswer && !player.hasSubmitted) {
            if (process.env.NODE_ENV === 'development') {
              logger.debug('[SHOWANSWER] 이미 채점된 문제에서 hasSubmitted 업데이트:', {
                playerId: player.id,
                nickname: player.nickname,
                currentAnswer: rawAnswer,
                questionId: currentQuestion.id
              });
            }
            return {
              ...player,
              hasSubmitted: true
            };
          }
          
          return player;
        });
      }

      const answerRevealTime = latest.gameSettings?.answerRevealTime ?? state.gameSettings.answerRevealTime ?? 10;
      const nowMs = Date.now();
      const newState: any = {
        gameState: 'showingAnswer',
        phaseStartedAt: nowMs,
        phaseDuration: answerRevealTime,
        paused: false,
        players: gradedPlayers
      };
      if (!alreadyGraded) {
        newState.lastGradedQuestionId = currentQuestion.id;
      }

      draftAnswersRef.current.clear();

      setState(prev => ({ ...prev, ...newState }));
      syncManager.updateGameData(newState);
      
      // 답안이 있는 모든 플레이어의 hasSubmitted 상태를 서버에 명시적으로 동기화
      // 네트워크 지연이나 동기화 문제로 인한 누락 방지
      const playersWithValidAnswers = gradedPlayers.filter(p => {
        const hasAnswer = p.currentAnswer !== undefined && 
                         p.currentAnswer !== null && 
                         p.currentAnswer !== '';
        return hasAnswer;
      });
      
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[SHOWANSWER] 채점 완료, 동기화 시작:', {
          totalPlayers: gradedPlayers.length,
          playersWithAnswers: playersWithValidAnswers.length,
          playersWithSubmitted: gradedPlayers.filter(p => p.hasSubmitted).length,
          alreadyGraded,
          questionId: currentQuestion.id,
          questionScore: currentQuestion.score,
          lastGradedQuestionId: latest.lastGradedQuestionId
        });
      }
      
      // 모든 채점된 플레이어의 상태를 동기화 (이미 채점된 경우는 스킵)
      // 이미 채점된 경우는 서버에 이미 동기화되어 있으므로 중복 신호 방지
      if (!alreadyGraded) {
        syncPlayerSubmissions(gradedPlayers);
      } else {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[SHOWANSWER] 이미 채점됨, 동기화 스킵:', {
            questionId: currentQuestion.id,
            lastGradedQuestionId: latest.lastGradedQuestionId
          });
        }
      }
      
      // 채점 완료 후 검증: 모든 답안이 있는 플레이어가 hasSubmitted인지 확인
      const playersWithAnswersButNotSubmitted = playersWithValidAnswers.filter(p => !p.hasSubmitted);
      if (playersWithAnswersButNotSubmitted.length > 0) {
        logger.warn('[SHOWANSWER] 답안이 있지만 hasSubmitted가 false인 플레이어:', {
          count: playersWithAnswersButNotSubmitted.length,
          players: playersWithAnswersButNotSubmitted.map(p => ({
            id: p.id,
            nickname: p.nickname,
            currentAnswer: p.currentAnswer,
            hasSubmitted: p.hasSubmitted
          }))
        });
      }

      if (latest.gameSettings?.autoMode) {
        setAutoTimer(answerRevealTime, () => {
          const nextState = syncManager.getGameData();
          if (nextState.gameState === 'showingAnswer' && !nextState.paused) {
            actions.nextQuestion();
          }
        });
      }
    },

    endGame: () => {
      // 자동 진행 타이머 정리
      clearAllAutoProgressTimers();
      draftAnswersRef.current.clear();
      clearDraftSyncTimers();
      
      // 게임 종료 시 결과 보기 표시 (finished 상태로 전환)
      // 점수는 유지하고, 제출 상태만 초기화
      const currentState = syncManager.getGameData();
      
      // 점수는 유지하고, 제출 상태와 답안만 초기화
      // 탈락 상태도 유지 (게임 종료 시점의 상태를 보여주기 위해)
      const finalPlayers = (currentState.players || []).map(p => {
        // 점수가 명시적으로 존재하는지 확인
        const preservedScore = typeof p.score === 'number' ? p.score : 0;
        return {
          ...p,
          score: preservedScore, // 점수 명시적으로 보존
          // isEliminated도 유지 (현재 탈락 상태 유지)
          hasSubmitted: false, // 제출 상태만 초기화
          currentAnswer: undefined // 답안 초기화
        };
      });

      const totalScore = finalPlayers.reduce((sum, p) => sum + p.score, 0);
      logger.debug('[ENDGAME] 게임 종료:', {
        playersCount: finalPlayers.length,
        totalScore: totalScore,
        players: finalPlayers.map(p => ({ id: p.id, nickname: p.nickname, score: p.score }))
      });
      
      // 점수가 모두 0이면 경고 (정상적인 경우일 수도 있지만 확인 필요)
      if (totalScore === 0 && finalPlayers.length > 0) {
        console.warn('[ENDGAME] 경고: 모든 플레이어의 점수가 0입니다. 이는 정상일 수도 있습니다.');
      }

      const newState = {
        gameState: 'finished' as const,
        paused: false,
        hasStarted: false,
        phaseStartedAt: null,
        phaseDuration: null,
        currentQuestionIndex: currentState.currentQuestionIndex, // 현재 문제 인덱스 유지
        players: finalPlayers, // 점수가 유지된 플레이어 데이터
        pausedPrevState: null
      };
      
      // 상태 업데이트와 Firestore 업데이트를 하나의 배치로 처리 (경합 방지)
      setState(prev => ({ ...prev, ...newState }));
      // 중요 업데이트: gameState, players 모두 포함 → 버전 증가
      syncManager.updateGameData(newState as any);
    },

    eliminatePlayer: (playerId: string) => {
      const currentState = syncManager.getGameData();
      const serverPlayer = currentState.players.find(p => p.id === playerId);
      
      // 서버의 현재 값과 비교하여 다를 때만 동기화 (중복 신호 방지)
      if (serverPlayer && serverPlayer.isEliminated === true) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[ELIMINATE] 서버 값과 동일, 동기화 스킵:', { playerId });
        }
        return;
      }
      
      const updatedPlayers = currentState.players.map(player =>
        player.id === playerId ? { ...player, isEliminated: true } : player
      );
      setState(prev => ({ ...prev, players: updatedPlayers }));
      syncManager.updatePlayer(playerId, { isEliminated: true });
    },

    revivePlayer: (playerId: string) => {
      const currentState = syncManager.getGameData();
      const serverPlayer = currentState.players.find(p => p.id === playerId);
      
      // 서버의 현재 값과 비교하여 다를 때만 동기화 (중복 신호 방지)
      if (serverPlayer && serverPlayer.isEliminated === false) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[REVIVE] 서버 값과 동일, 동기화 스킵:', { playerId });
        }
        return;
      }
      
      const updatedPlayers = currentState.players.map(player =>
        player.id === playerId ? { ...player, isEliminated: false } : player
      );
      setState(prev => ({ ...prev, players: updatedPlayers }));
      syncManager.updatePlayer(playerId, { isEliminated: false });
    },

    kickPlayer: async (playerId: string) => {
      
      try {
        await syncManager.kickPlayer(playerId);
        const currentState = syncManager.getGameData();
        const updatedPlayers = currentState.players.filter(player => player.id !== playerId);
        setState(prev => ({ ...prev, players: updatedPlayers }));
        
      } catch (error) {
        logger.error('플레이어 강퇴 실패:', error);
        throw error;
      }
    },

    submitAnswer: (playerId: string, answer: string | number) => {
      const currentState = syncManager.getGameData();
      // 이미 제출한 플레이어는 중복 제출 방지
      const player = currentState.players.find(p => p.id === playerId);
      if (player && player.hasSubmitted) {
        logger.debug('[SUBMIT] 이미 제출된 플레이어:', playerId);
        return;
      }
      
      // draft 타이머 정리
      const pendingTimer = draftSyncTimersRef.current.get(playerId);
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        draftSyncTimersRef.current.delete(playerId);
      }
      
      // 답안을 문자열로 변환 (일관성 유지)
      // 객관식의 경우 숫자 인덱스를 문자열로 변환 (예: 0 -> "0")
      const answerStr = answer !== null && answer !== undefined 
        ? String(answer) 
        : '';
      
      // draft answer 제거 (제출된 답안이 우선)
      draftAnswersRef.current.delete(playerId);

      logger.debug('[SUBMIT] 답안 제출:', { playerId, answer, answerStr });

      const updatedPlayers = currentState.players.map(player =>
        player.id === playerId 
          ? { ...player, hasSubmitted: true, currentAnswer: answerStr }
          : player
      );
      setState(prev => ({ ...prev, players: updatedPlayers }));
      
      // 서버의 현재 값과 비교하여 다를 때만 동기화 (중복 신호 방지)
      const serverPlayer = currentState.players.find(p => p.id === playerId);
      if (serverPlayer && 
          serverPlayer.hasSubmitted === true && 
          serverPlayer.currentAnswer === answerStr) {
        // 이미 동일한 값으로 동기화되어 있으면 스킵
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[SUBMIT] 서버 값과 동일, 동기화 스킵:', {
            playerId,
            answerStr
          });
        }
        return;
      }
      
      // 서버에는 제출 시점에만 전송 (트래픽/스냅샷 최소화)
      syncManager.updatePlayer(playerId, { hasSubmitted: true, currentAnswer: answerStr } as any)
        .catch(error => {
          logger.error('[SUBMIT] 답안 제출 실패:', error);
        });
    },

    gradeCurrentQuestion: () => {
      const latest = syncManager.getGameData();
      const currentQuestion = latest.questions[latest.currentQuestionIndex];
      
      if (!currentQuestion || latest.lastGradedQuestionId === currentQuestion.id) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[GRADE] 채점 스킵:', {
            hasQuestion: !!currentQuestion,
            alreadyGraded: latest.lastGradedQuestionId === currentQuestion.id,
            questionId: currentQuestion?.id,
            lastGradedQuestionId: latest.lastGradedQuestionId
          });
        }
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[GRADE] 채점 시작:', {
          questionId: currentQuestion.id,
          questionType: currentQuestion.type,
          questionScore: currentQuestion.score,
          correctAnswer: currentQuestion.correctAnswer,
          playersCount: latest.players?.length || 0,
          currentQuestionIndex: latest.currentQuestionIndex
        });
      }
      
      // draft answer도 고려하여 채점 (공통 함수 사용)
      const playersWithAnswers = (latest.players || []).map(player => {
        const merged = mergePlayerAnswers(player, draftAnswersRef.current, currentQuestion);
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[GRADE] 답안 통합:', {
            playerId: player.id,
            nickname: player.nickname,
            originalAnswer: player.currentAnswer,
            draftAnswer: draftAnswersRef.current.get(player.id),
            mergedAnswer: merged.currentAnswer
          });
        }
        return merged;
      });
      
      // 플레이어 채점 (공통 함수 사용)
      const gradedPlayers = playersWithAnswers.map(player => {
        const scoreBefore = player.score;
        const graded = gradePlayer(player, currentQuestion, latest.gameSettings || state.gameSettings);
        
        // 점수 증가량 검증 (중복 점수 부여 방지)
        const scoreIncrease = graded.score - scoreBefore;
        const expectedIncrease = graded.score > scoreBefore ? currentQuestion.score : 0;
        
        // 점수 증가량이 예상과 다르면 경고 및 수정
        if (scoreIncrease !== expectedIncrease && scoreIncrease > 0) {
          logger.warn('[GRADE] 점수 증가량 이상, 수정:', {
            playerId: player.id,
            nickname: player.nickname,
            scoreBefore,
            scoreAfter: graded.score,
            scoreIncrease,
            expectedIncrease,
            questionScore: currentQuestion.score
          });
          // 잘못된 점수 증가량이면 예상 증가량으로 수정
          const correctedScore = scoreBefore + expectedIncrease;
          return {
            ...graded,
            score: correctedScore
          };
        }
        
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[GRADE] 플레이어 채점:', {
            playerId: player.id,
            nickname: player.nickname,
            questionType: currentQuestion.type,
            questionId: currentQuestion.id,
            rawAnswer: player.currentAnswer,
            answerToCheck: player.currentAnswer,
            hasAnswer: hasValidAnswer(player.currentAnswer, currentQuestion.type),
            correctAnswer: currentQuestion.correctAnswer,
            isCorrect: graded.score > player.score,
            scoreBefore: player.score,
            scoreAfter: graded.score,
            scoreIncrease,
            hasSubmittedBefore: player.hasSubmitted,
            hasSubmittedAfter: graded.hasSubmitted
          });
        }
        
        return graded;
      });
      
      // 채점 완료 후 검증
      const playersWithAnswersButNotSubmitted = gradedPlayers.filter(p => {
        const hasAnswer = p.currentAnswer !== undefined && 
                         p.currentAnswer !== null && 
                         p.currentAnswer !== '';
        return hasAnswer && !p.hasSubmitted;
      });
      
      if (playersWithAnswersButNotSubmitted.length > 0) {
        logger.warn('[GRADE] 답안이 있지만 hasSubmitted가 false인 플레이어:', {
          count: playersWithAnswersButNotSubmitted.length,
          players: playersWithAnswersButNotSubmitted.map(p => ({
            id: p.id,
            nickname: p.nickname,
            currentAnswer: p.currentAnswer,
            hasSubmitted: p.hasSubmitted
          }))
        });
      }
      
      // 상태 업데이트
      setState(prev => ({ ...prev, players: gradedPlayers }));
      syncManager.updateGameData({
        players: gradedPlayers as any,
        lastGradedQuestionId: currentQuestion.id
      } as any);
      
      // 답안이 있는 모든 플레이어의 hasSubmitted 상태를 서버에 명시적으로 동기화
      // 네트워크 지연이나 동기화 문제로 인한 누락 방지
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[GRADE] 채점 완료, 동기화 시작:', {
          totalPlayers: gradedPlayers.length,
          playersWithAnswers: gradedPlayers.filter(p => {
            const hasAnswer = p.currentAnswer !== undefined && 
                             p.currentAnswer !== null && 
                             p.currentAnswer !== '';
            return hasAnswer;
          }).length,
          playersWithSubmitted: gradedPlayers.filter(p => p.hasSubmitted).length,
          questionId: currentQuestion.id
        });
      }
      syncPlayerSubmissions(gradedPlayers);
      
      syncManager.broadcast('FINALIZE_ANSWERS', { questionId: currentQuestion.id, players: gradedPlayers });
      
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[GRADE] 채점 완료:', {
          questionId: currentQuestion.id,
          gradedCount: gradedPlayers.length,
          lastGradedQuestionId: currentQuestion.id
        });
      }
    },

    checkAnswer: checkAnswer,

    updateGameSettings: (settings: Partial<GameSettings>) => {
      const currentState = syncManager.getGameData();
      
      // 경계값 검증: 음수/0/너무 큰 값 체크
      const validateTimeValue = (value: number | undefined, fieldName: string): number | undefined => {
        if (value === undefined) return undefined;
        if (value < 1) {
          setState(prev => ({ ...prev, error: `${fieldName}은(는) 최소 1초 이상이어야 합니다.` }));
          return undefined; // 무효한 값은 무시
        }
        if (value > 3600) {
          setState(prev => ({ ...prev, error: `${fieldName}은(는) 최대 3600초(1시간)까지 설정 가능합니다.` }));
          return undefined; // 무효한 값은 무시
        }
        return value;
      };
      
      const validatedTimeLimit = validateTimeValue(settings.timeLimit, '문제 제한 시간');
      const validatedAnswerRevealTime = validateTimeValue(settings.answerRevealTime, '정답 공개 시간');
      
      // 검증 실패 시 업데이트 중단
      if (settings.timeLimit !== undefined && validatedTimeLimit === undefined) {
        return;
      }
      if (settings.answerRevealTime !== undefined && validatedAnswerRevealTime === undefined) {
        return;
      }
      
      const validatedSettings = {
        ...settings,
        ...(validatedTimeLimit !== undefined && { timeLimit: validatedTimeLimit }),
        ...(validatedAnswerRevealTime !== undefined && { answerRevealTime: validatedAnswerRevealTime })
      };
      
      const newSettings = { ...currentState.gameSettings, ...validatedSettings };
      setState(prev => ({ ...prev, gameSettings: newSettings, error: null }));
      
      if ((currentState.gameState === 'playing' || currentState.gameState === 'showingAnswer' || currentState.gameState === 'paused') && 
          (validatedSettings.timeLimit !== undefined || validatedSettings.answerRevealTime !== undefined)) {
        const nowMs = Date.now();
        const elapsed = currentState.phaseStartedAt ? Math.floor((nowMs - currentState.phaseStartedAt) / 1000) : 0;
        const currentRemain = Math.max(0, (currentState.phaseDuration || 0) - elapsed);
        const maxTime = currentState.gameState === 'showingAnswer' 
          ? (validatedSettings.answerRevealTime ?? currentState.gameSettings.answerRevealTime)
          : (validatedSettings.timeLimit ?? currentState.gameSettings.timeLimit);
        
        // 경계값 재검증: 새 남은 시간이 유효한 범위 내인지 확인
        const validatedRemain = Math.max(1, Math.min(Math.max(1, maxTime), Math.min(currentRemain, maxTime)));
        const newRemain = Math.min(validatedRemain, maxTime);
        
        if (newRemain !== currentRemain && newRemain >= 1 && newRemain <= 3600) {
          const updates = {
            phaseStartedAt: nowMs, // ms 단위 타임스탬프
            phaseDuration: newRemain // 초 단위
          };
          setState(prev => ({ ...prev, ...updates }));
          syncManager.updateGameData(updates as any);
        }
      }
      
      if (settings.autoMode !== undefined && settings.autoMode !== currentState.gameSettings.autoMode) {
        const nowMs = Date.now();
        const targetState = currentState.gameState === 'paused' 
          ? (currentState.pausedPrevState || 'playing')
          : currentState.gameState;
        const newDuration = targetState === 'showingAnswer' 
          ? newSettings.answerRevealTime 
          : newSettings.timeLimit;
        
        // 경계값 재검증
        if (newDuration < 1 || newDuration > 3600) {
          setState(prev => ({ ...prev, error: '타이머 설정은 1초 이상 3600초 이하여야 합니다.' }));
          return;
        }
        
        const combinedUpdates = {
          gameSettings: newSettings,
          phaseStartedAt: nowMs, // ms 단위 타임스탬프
          phaseDuration: newDuration, // 초 단위
          paused: false
        };
        
        setState(prev => ({ ...prev, ...combinedUpdates }));
        syncManager.updateGameData(combinedUpdates as any);
        
        if (settings.autoMode && newDuration > 0) {
          setAutoTimer(newDuration, () => {
            const latest = syncManager.getGameData();
            if (latest.gameState === targetState && !latest.paused) {
              if (targetState === 'playing') {
                actions.showAnswer();
              } else if (targetState === 'showingAnswer') {
                actions.nextQuestion();
              }
            }
          });
        } else {
          clearAllAutoProgressTimers();
        }
      } else {
        syncManager.updateGameData({ gameSettings: newSettings });
      }
    },

    resetGame: () => {
      setState(initialState);
      draftAnswersRef.current.clear();
      clearDraftSyncTimers();
      try {
        const current = syncManager.getGameData?.();
        const hasRoom = !!(current && current.room && (current.room as any).code);
        if (!hasRoom) {
          console.warn('[RT] Room ID가 없어 resetGame에서 Firestore 업데이트를 생략합니다');
          return;
        }
        syncManager.updateGameData({
          room: null,
          players: [],
          questions: [],
          gameState: 'waiting',
          currentQuestionIndex: 0,
          hasStarted: false,
          phaseStartedAt: null,
          phaseDuration: null,
          paused: false,
          pausedPrevState: null
        });
      } catch (e) {
        console.warn('[RT] resetGame Firestore 업데이트 중 예외 발생, 업데이트를 생략합니다', e);
      }
    },

    updateHostActivity: async (roomCode: string) => {
      // 호스트 활동 업데이트는 FirestoreSyncManager에서 처리
      await syncManager.broadcast('HOST_ACTIVITY', { roomCode, sessionId: 'current_session' });
    },

    adjustTime: (delta: number) => {
      const currentState = syncManager.getGameData();
      if (currentState.gameState !== 'playing' && currentState.gameState !== 'showingAnswer' && currentState.gameState !== 'paused') return;
      
      const nowMs = Date.now();
      
      // calculateRemainingTime 헬퍼 함수 사용
      const currentRemainingTime = calculateRemainingTime(
        currentState.phaseStartedAt,
        currentState.phaseDuration,
        currentState.paused
      );
      
      // 새로운 남은 시간 계산 (안전한 조정)
      let newRemainingTime;
      if (delta < 0) {
        // 감소 요청인 경우: 현재 시간보다 많이 감소시키려면 안전하게 조정
        const requestedDecrease = Math.abs(delta);
        const safeDecrease = Math.min(requestedDecrease, currentRemainingTime - 1); // 최소 1초는 남기기
        newRemainingTime = Math.max(1, currentRemainingTime - safeDecrease);
      } else {
        // 증가 요청인 경우: 그대로 적용
        newRemainingTime = currentRemainingTime + delta;
      }
      
      // phaseStartedAt을 현재 시간으로, phaseDuration을 새로운 남은 시간으로 재설정
      const updates = {
        phaseStartedAt: nowMs, // ms 단위 타임스탬프
        phaseDuration: newRemainingTime, // 초 단위
        paused: false // 시간 조정 시 일시정지 해제
      };
      
      setState(prev => ({ ...prev, ...updates }));
      syncManager.updateGameData(updates as any);
      
      // 자동 진행 타이머 재설정
      if (currentState.gameSettings?.autoMode && newRemainingTime > 0) {
        const targetState = currentState.gameState === 'paused' 
          ? (currentState.pausedPrevState || 'playing')
          : currentState.gameState;
        
        setAutoTimer(newRemainingTime, () => {
          const latest = syncManager.getGameData();
          if (latest.gameState === targetState && !latest.paused) {
            if (targetState === 'playing') {
              actions.showAnswer();
            } else if (targetState === 'showingAnswer') {
              actions.nextQuestion();
            }
          }
        });
      }
    }
  };

  // 호스트 측 안전장치: 타이머가 실행되지 않을 때 강제 처리 (단순화)
  useEffect(() => {
    const currentState = syncManager.getGameData();
    if (!isHost) return;
    if (currentState.gameState !== 'showingAnswer') return;
    if (currentState.paused) return;
    if (!currentState.phaseStartedAt || currentState.phaseDuration == null) return;

    const elapsedSec = Math.floor((Date.now() - currentState.phaseStartedAt) / 1000);
    const due = elapsedSec >= currentState.phaseDuration;
    if (!due) return;

    actions.nextQuestion();
  }, [isHost, state.gameState, state.phaseStartedAt, state.phaseDuration, state.paused, state.currentQuestionIndex, state.questions.length, actions]);

  const value: GameContextValue = {
    state,
    actions
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
}

export function useNewGameContext(): GameContextValue {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useNewGameContext must be used within a NewGameProvider');
  }
  return context;
}

export default GameContext;
